import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Space, Button, Modal, Input, notification, Dropdown, Menu, Form, Typography } from 'antd';
import { LoginOutlined, MenuOutlined, UserAddOutlined } from '@ant-design/icons';
import { auth, database, ref, get } from '../../pages/Auth/db/firebase';
import UserDropdown from './UserDropdown';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { loginCashier } from '../../utils/cashierSession';

// ========== MOBILE BURGER MENU FEATURE ==========
const AppHeader = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [cashierLoginOpen, setCashierLoginOpen] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [cashierForm] = Form.useForm();
  const { Paragraph } = Typography;

  const location = useLocation();
  const navigate = useNavigate();

  // Don't show header on login/register pages
  const hideHeaderPaths = ['/login', '/register'];
  const shouldHideHeader = hideHeaderPaths.includes(location.pathname);

  // Extract facilityId from current path for cashier login
  const facilityId = location.pathname.split('/').filter(Boolean)[0];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          // Try to fetch user data from database
          const userSnapshot = await get(ref(database, 'users'));
          if (userSnapshot.exists()) {
            const facilities = userSnapshot.val();
            let foundUserData = null;

            // Check 'me' collection first (for superadmin)
            if (facilities.me && facilities.me.email === currentUser.email) {
              foundUserData = { ...facilities.me, facilityId: 'me' };
            } else {
              // Check all facilities
              for (const facilityId in facilities) {
                if (facilityId === 'me') continue;
                const users = facilities[facilityId];
                for (const uid in users) {
                  if (users[uid].email === currentUser.email) {
                    foundUserData = { ...users[uid], facilityId };
                    break;
                  }
                }
                if (foundUserData) break;
              }
            }
            
            // Check if account is held and force logout
            if (foundUserData && foundUserData.accountHeld) {
              api.error({ 
                message: 'Account Suspended',
                description: 'Your account has been suspended by an administrator. You have been logged out.',
                duration: 0
              });
              await auth.signOut();
              navigate('/login');
              return;
            }

            setUserData(foundUserData);
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
      } else {
        setUserData(null);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cashierLoginOpen) {
      cashierForm.resetFields();
    }
  }, [cashierLoginOpen, cashierForm]);

  const handleCashierLogin = async () => {
    try {
      const { email, password } = await cashierForm.validateFields();
      if (!facilityId) {
        api.warning({ message: 'Choose your shop', description: 'Open your shop URL to start a cashier session.' });
        return;
      }
      setLoggingIn(true);
      await loginCashier(facilityId, email, password);
      setCashierLoginOpen(false);
      cashierForm.resetFields();
      navigate(`/${facilityId}/cashier`);
      api.success({ message: 'Cashier session ready', description: 'You are now connected to your checkout.' });
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      api.error({ message: 'Login failed', description: error?.message || 'Unable to log in with those details.' });
    } finally {
      setLoggingIn(false);
    }
  };

  const resetCashierLogin = () => {
    cashierForm.resetFields();
    setCashierLoginOpen(false);
  };

  // Show cashier login only on public facility pages (not auth-protected routes)
  const isPublicFacilityPage = facilityId && !location.pathname.includes('/admin') && !location.pathname.includes('/user') && !location.pathname.includes('/superadmin');

  if (shouldHideHeader) {
    return null;
  }

  return (
    <>
      {contextHolder}
      <div className="app-header-container">
        <div className="app-header-content">
          <div className="app-header-brand">
            <img 
              src="/logo_lightmode.png" 
              alt="KioStock" 
              className="app-header-logo light-logo"
              style={{ height: '32px', width: 'auto' }}
            />
            <img 
              src="/logo_darkmode.png" 
              alt="KioStock" 
              className="app-header-logo dark-logo"
              style={{ height: '32px', width: 'auto' }}
            />
          </div>
          
          {/* Desktop Actions */}
          <div className="app-header-actions app-header-desktop">
            <Space size={8}>
              {isPublicFacilityPage && (
                <Button 
                  icon={<LoginOutlined />} 
                  onClick={() => setCashierLoginOpen(true)}
                  title="Cashier Login"
                  size="middle"
                >
                  <span className="app-header-button-text">
                    Cashier
                  </span>
                </Button>
              )}
              {!user && (
                <Button
                  type="default"
                  onClick={() => navigate('/login')}
                  size="middle"
                >
                  Login
                </Button>
              )}
              {user && userData?.role === 'superadmin' && <NotificationBell />}
              {user && <UserDropdown userData={userData} />}
              <ThemeToggle />
            </Space>
          </div>

          {/* Mobile Burger Menu */}
          <div className="app-header-mobile">
            <Dropdown 
              overlay={
                <Menu>
                  {!user && (
                    <Menu.Item key="login" onClick={() => navigate('/login')}>Login</Menu.Item>
                  )}
                  {isPublicFacilityPage && (
                    <Menu.Item 
                      key="cashier"
                      icon={<LoginOutlined />}
                      onClick={() => setCashierLoginOpen(true)}
                    >
                      Cashier Login
                    </Menu.Item>
                  )}
                  {user && userData?.role === 'superadmin' && (
                    <Menu.Item key="notifications">
                      <NotificationBell />
                    </Menu.Item>
                  )}
                  {user && (
                    <Menu.Item key="user">
                      <UserDropdown userData={userData} />
                    </Menu.Item>
                  )}
                  <Menu.Item key="theme">
                    <Space>
                      <span style={{ color: 'var(--text-secondary)' }}>Theme:</span>
                      <ThemeToggle />
                    </Space>
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                type="text"
                icon={<MenuOutlined style={{ fontSize: '18px' }} />}
                style={{ 
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Cashier Login Modal */}
      <Modal
        title="Cashier Login"
        open={cashierLoginOpen}
        onCancel={resetCashierLogin}
        onOk={handleCashierLogin}
        okText="Login"
        confirmLoading={loggingIn}
        width={420}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Paragraph style={{ marginBottom: 0 }}>
                <UserAddOutlined style={{ fontSize: 50, textAlign: 'center' }} />
                <h3>Login to your workspace</h3>
          </Paragraph>
          <Form form={cashierForm} layout="vertical" onFinish={handleCashierLogin}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Enter your email' },
                { type: 'email', message: 'Provide a valid email' }
              ]}
            >
              <Input placeholder="cashier@example.com" autoComplete="email" />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Enter your password' }]}
            >
              <Input.Password placeholder="Enter your password" autoComplete="current-password" onPressEnter={handleCashierLogin} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default AppHeader;
